import { IPaginatedData, IPaginatorParams, TableSort } from '@/types/table';
import { AppUtils } from '@/utils/AppUtils.ts';

export class TableUtils {
    public static sort2String<T>(sort?: TableSort<T>): string | undefined {
        if (!sort) {
            return undefined;
        }

        return Object.keys(sort)
            .map((k) => {
                return `${k}:${sort[k as keyof TableSort<T>]}`;
            })
            .join(',');
    }

    public static paginateArrayV<T>(
        allItems: T[] = [],
        paginator: IPaginatorParams<T>,
    ): IPaginatedData<T> {
        const data = allItems ?? [];
        const limit = Math.max(1, paginator.limit ?? 50);
        const total = data.length;
        const currentPage = Math.max(1, paginator.page ?? 1);
        const totalPages = Math.ceil(total / limit) || 0;

        const processedItems = [...data]; 

        const sort = paginator?.sort;
        if (sort && Object.keys(sort).length > 0) {
            const sortKeys = Object.keys(sort) as (keyof T)[];

            processedItems.sort((a, b) => {
                for (const key of sortKeys) {
                    const direction = sort[key];
                    const modifier = direction === 'asc' ? 1 : -1;

                    const valA = a[key];
                    const valB = b[key];

                    if (typeof valA === 'string' && typeof valB === 'string') {
                        const cmp = valA.localeCompare(valB, undefined, {
                            sensitivity: 'accent',
                            numeric: true,
                        });
                        if (cmp !== 0) return cmp * modifier;
                    }
                    else {
                        if (valA > valB) return 1 * modifier;
                        if (valA < valB) return -1 * modifier;
                    }
                }

                return 0;
            });
        }

        const startIndex = (currentPage - 1) * limit;
        const items = processedItems.slice(startIndex, startIndex + limit);

        return {
            limit,
            total,
            currentPage,
            totalPages,
            items,
        };
    }

    public static paginateArray<T>(
        allItems: T[],
        paginator: IPaginatorParams<T>,
    ): IPaginatedData<T> {
        const limit = paginator.limit ?? 50;
        const total = allItems?.length;
        const currentPage = paginator.page ?? 1;
        const totalPages = Math.ceil(total / limit);

        const sort = paginator?.sort ?? ({} as TableSort<T>);
        if (Object.keys(sort ?? {}).length > 0) {
            allItems = (Object.keys(sort) as (keyof T)[]).reduce<T[]>(
                (a, b) => {
                    return AppUtils.sortBy<T>(a, b, sort[b]);
                },
                allItems,
            );
        }

        const startIndex = (currentPage - 1) * limit;
        const endIndex = startIndex + limit;
        const items = allItems?.slice(startIndex, endIndex);

        return {
            limit,
            total,
            currentPage,
            totalPages,
            items,
        };
    }
}
